// File: frontend/src/pages/fees/FeeReceipt.jsx

import React from 'react';
import { useSchool } from '../../context/SchoolContext';

const FeeReceipt = ({ receiptData, onClose }) => {
  const { school } = useSchool();
  if (!receiptData) return null;

  const {
    receipt_no,
    payment_date,
    payment_method = 'cash',
    student_name,
    student_code,
    father_name,
    class_id,
    total_amount = 0,
    discount_amount = 0,
    fine_amount = 0,
    paid_amount = 0,
    balance_amount = 0,
    past_arrears = 0,
    total_outstanding_dues = 0,
    change_due = 0,
    payment_history = [],
    remarks
  } = receiptData;

  const total = parseFloat(total_amount) || 0;
  const discount = parseFloat(discount_amount) || 0;
  const fine = parseFloat(fine_amount) || 0;
  const paid = parseFloat(paid_amount) || 0;
  const net = total - discount + fine;
  const currentBalance = parseFloat(balance_amount) || Math.max(0, net - paid);
  const arrears = parseFloat(past_arrears) || 0;
  const totalDues = parseFloat(total_outstanding_dues) || (currentBalance + arrears);
  const change = parseFloat(change_due) || 0;

  const formattedDate = payment_date ? new Date(payment_date).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : new Date().toLocaleDateString();

  const formatMoney = (val) => {
    const num = parseFloat(val) || 0;
    return `PKR ${num.toLocaleString('en-PK', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-start justify-center p-4 overflow-y-auto receipt-modal-overlay">
      
      {/* Print CSS Rules */}
      <style>{`
        @media print {
          @page {
            size: A4 portrait;
            margin: 6mm;
          }
          body {
            background: white !important;
            color: black !important;
          }
          /* Hide all background app pages, sidebar, header & elements during print */
          #root > div > main,
          #root > div > header,
          #root > div > aside,
          body > div:not(.receipt-modal-overlay),
          .no-print {
            display: none !important;
          }
          .receipt-modal-overlay {
            position: static !important;
            display: block !important;
            background: white !important;
            padding: 0 !important;
            margin: 0 !important;
            overflow: visible !important;
            width: 100% !important;
            height: auto !important;
          }
          .receipt-print-area {
            position: static !important;
            display: block !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            border: none !important;
            background: white !important;
          }
        }
      `}</style>

      <div className="bg-white rounded-2xl max-w-3xl w-full p-6 sm:p-8 shadow-2xl border border-slate-200 receipt-print-area my-6">
        
        {/* Top Control Bar (Non-Printable) */}
        <div className="no-print flex items-center justify-between border-b pb-4 mb-6">
          <div>
            <h3 className="font-extrabold text-lg text-slate-800">Fee Payment Receipt & Account Statement</h3>
            <p className="text-xs text-slate-500">Official student receipt with multi-month payment history</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => window.print()}
              style={{ backgroundColor: school?.button_color || 'var(--button-color)' }}
              className="px-5 py-2 text-white font-bold rounded-xl text-sm shadow-md transition hover:brightness-110 flex items-center gap-2"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
              </svg>
              Print Receipt (A4)
            </button>
            {onClose && (
              <button onClick={onClose} className="font-bold text-slate-400 hover:text-slate-600 text-lg px-2">✕</button>
            )}
          </div>
        </div>

        {/* RECEIPT CONTENT BOX */}
        <div className="border-2 border-slate-900 p-6 space-y-5 font-serif text-slate-900">
          
          {/* 1. TOP ZONE: Header Banner */}
          <div className="border-b border-slate-900 pb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-[10px] font-sans font-extrabold uppercase tracking-widest bg-slate-900 text-white px-2.5 py-0.5 rounded">
                OFFICIAL STUDENT FEE RECEIPT
              </span>
              <span className="text-xs font-sans font-bold text-slate-700">Receipt #: <strong className="text-slate-900">{receipt_no}</strong></span>
            </div>

            <div className="flex items-center justify-between gap-4 mt-2">
              <div className="flex items-center gap-3">
                {school?.logo ? (
                  <img src={school.logo} alt="Logo" className="w-12 h-12 object-contain" />
                ) : (
                  <span className="text-3xl">🎓</span>
                )}
                <div>
                  <h1 className="text-lg font-bold uppercase tracking-wide font-sans">{school?.name || 'MarkazSchool Pro'}</h1>
                  <p className="text-xs font-sans text-slate-600">{school?.address || 'Main Campus, Educational District'} | Ph: {school?.phone || '+92 300 1234567'}</p>
                </div>
              </div>

              {/* Prominent Amount Paid Today Badge */}
              <div className="no-print-badge text-right bg-emerald-50 border border-emerald-300 rounded-xl px-4 py-2 font-sans">
                <span className="text-[10px] font-bold text-emerald-800 uppercase block tracking-wider">Amount Paid Today</span>
                <span className="text-lg font-extrabold text-emerald-700">{formatMoney(paid)}</span>
              </div>
            </div>
          </div>

          {/* Student & Transaction Info Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs font-sans border-b border-slate-300 pb-4 bg-slate-50 p-3 rounded-xl">
            <div><strong className="text-slate-500 block text-[10px] uppercase">Student Code</strong> <span className="font-bold text-slate-900">{student_code || receiptData.student_id}</span></div>
            <div><strong className="text-slate-500 block text-[10px] uppercase">Student Name</strong> <span className="font-bold text-slate-900">{student_name}</span></div>
            <div><strong className="text-slate-500 block text-[10px] uppercase">Class / Section</strong> <span className="font-bold text-slate-900">Class {class_id}</span></div>
            <div><strong className="text-slate-500 block text-[10px] uppercase">Father / Guardian</strong> <span className="font-semibold text-slate-800">{father_name || 'N/A'}</span></div>
            <div><strong className="text-slate-500 block text-[10px] uppercase">Payment Date</strong> <span className="font-semibold text-slate-800">{formattedDate}</span></div>
            <div><strong className="text-slate-500 block text-[10px] uppercase">Payment Method</strong> <span className="font-bold capitalize text-indigo-700">{payment_method}</span></div>
          </div>

          {/* Current Transaction Financial Itemization */}
          <div>
            <h3 className="text-xs font-sans font-bold uppercase tracking-wider text-slate-700 mb-2">Today's Fee Collection Itemization</h3>
            <table className="w-full text-left text-xs font-sans border-collapse border border-slate-300">
              <thead>
                <tr className="bg-slate-100 border-b border-slate-300 font-bold text-slate-700">
                  <th className="py-2 px-3">Description</th>
                  <th className="py-2 px-3 text-right">Amount (PKR)</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                <tr>
                  <td className="py-2 px-3">Tuition & Academic Structure Fees</td>
                  <td className="py-2 px-3 text-right font-medium">{formatMoney(total)}</td>
                </tr>
                {discount > 0 && (
                  <tr>
                    <td className="py-1.5 px-3 text-emerald-700">Less: Concession / Discount</td>
                    <td className="py-1.5 px-3 text-right text-emerald-700 font-semibold">-{formatMoney(discount)}</td>
                  </tr>
                )}
                {fine > 0 && (
                  <tr>
                    <td className="py-1.5 px-3 text-amber-700">Add: Late Payment Fine</td>
                    <td className="py-1.5 px-3 text-right text-amber-700 font-semibold">+{formatMoney(fine)}</td>
                  </tr>
                )}
                <tr className="font-bold bg-slate-50 border-t border-slate-300">
                  <td className="py-2 px-3">Net Current Month Payable</td>
                  <td className="py-2 px-3 text-right text-slate-900">{formatMoney(net)}</td>
                </tr>
                <tr className="font-bold text-emerald-700 bg-emerald-50/50">
                  <td className="py-2 px-3">Amount Paid Today</td>
                  <td className="py-2 px-3 text-right">{formatMoney(paid)}</td>
                </tr>
                {change > 0 && (
                  <tr className="font-bold text-blue-700 bg-blue-50/50">
                    <td className="py-2 px-3">Change Due / Advance Credit</td>
                    <td className="py-2 px-3 text-right">{formatMoney(change)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* 2. MIDDLE ZONE: Account Statement (Last 4 Months History Table) */}
          {payment_history && payment_history.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-sans font-bold uppercase tracking-wider text-slate-700">Account Statement (Last 4 Months Ledger)</h3>
                <span className="text-[10px] font-sans text-slate-500">Student Payment History Log</span>
              </div>
              <table className="w-full text-left text-xs font-sans border-collapse border border-slate-300">
                <thead>
                  <tr className="bg-slate-100 border-b border-slate-300 font-bold text-slate-700">
                    <th className="py-1.5 px-3">Billing Month</th>
                    <th className="py-1.5 px-3 text-right">Billed</th>
                    <th className="py-1.5 px-3 text-right">Paid</th>
                    <th className="py-1.5 px-3 text-center">Status</th>
                    <th className="py-1.5 px-3 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-200">
                  {payment_history.map((h, index) => {
                    const st = String(h.status || '').toLowerCase();
                    let badgeBg = 'bg-red-100 text-red-800 border-red-200';
                    let badgeLabel = 'Unpaid';
                    if (st === 'paid' || parseFloat(h.balance) <= 0) {
                      badgeBg = 'bg-emerald-100 text-emerald-800 border-emerald-200';
                      badgeLabel = 'Paid';
                    } else if (st === 'partial') {
                      badgeBg = 'bg-amber-100 text-amber-800 border-amber-200';
                      badgeLabel = 'Partial';
                    }

                    return (
                      <tr key={h.id || index} className={index % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}>
                        <td className="py-1.5 px-3 font-semibold text-slate-800">{h.month_name}</td>
                        <td className="py-1.5 px-3 text-right text-slate-600">{formatMoney(h.total_amount)}</td>
                        <td className="py-1.5 px-3 text-right font-medium text-emerald-700">{formatMoney(h.paid_amount)}</td>
                        <td className="py-1.5 px-3 text-center">
                          <span className={`px-2 py-0.5 text-[10px] font-bold rounded-full border ${badgeBg}`}>
                            {badgeLabel}
                          </span>
                        </td>
                        <td className="py-1.5 px-3 text-right font-bold text-slate-900">{formatMoney(h.balance)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* 3. BOTTOM ZONE: Account Balance Summary */}
          <div className="bg-slate-900 text-white rounded-xl p-4 font-sans space-y-2 border border-slate-800">
            <div className="flex justify-between items-center text-xs text-slate-300">
              <span>Current Month Remaining Balance:</span>
              <span className="font-semibold text-white">{formatMoney(currentBalance)}</span>
            </div>
            <div className="flex justify-between items-center text-xs text-amber-300">
              <span>Past Arrears (Previous Months Unpaid Dues):</span>
              <span className="font-semibold">{formatMoney(arrears)}</span>
            </div>
            <div className="border-t border-slate-700 pt-2 flex justify-between items-center">
              <span className="text-sm font-extrabold tracking-wide uppercase text-white">TOTAL OUTSTANDING DUES:</span>
              <span className={`text-base font-extrabold ${totalDues > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                {formatMoney(totalDues)}
              </span>
            </div>
          </div>

          {remarks && (
            <p className="text-[11px] font-sans italic text-slate-600">Remarks / Audit Note: {remarks}</p>
          )}

          {/* Signatures */}
          <div className="pt-4 flex justify-between items-end text-xs font-sans">
            <div className="text-center border-t border-slate-400 pt-1 px-6 text-slate-600 font-medium">
              Cashier / Accountant Signature
            </div>
            <div className="text-center border-t border-slate-900 pt-1 px-8 font-bold flex flex-col items-center">
              {school?.principal_signature && (
                <img src={school.principal_signature} alt="Signature" className="h-7 max-w-[120px] object-contain mb-1" />
              )}
              <span>{school?.principal_name || 'Principal'} Signature</span>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default FeeReceipt;
